import { AgGridColumnType, AgGridExportType, ExportedColDef, ExportedColGroupDef } from '@/shared/models/ag-grid.model'
import { CommonLaboratoryResponse, CommonProtocolResponse } from '@/api/services/common.dto'
import { CommonPersonnelResponse } from '@/api/services/common/personnel/common-personnel.dto'
import { CommonPortionTypeResponse } from '@/api/services/common/portion-type/common-portion-type.dto'
import { CommonStatusResponse } from '@/api/services/common/status/common-status.dto'
import i18n from '@/lang/i18n'
import { StatisticalAnalysisExternalListCase, StatisticalAnalysisExternalListColumnsCases } from '@/modules/statistical-analysis-module/shared/external-list-columns/external-list-columns-cases'
import { StatisticalAnalysisExternalListColumnsSample, StatisticalAnalysisExternalListSample } from '@/modules/statistical-analysis-module/shared/external-list-columns/external-list-columns-samples'
import { ColDef, ColGroupDef } from '@ag-grid-enterprise/all-modules'
import { concat, join } from 'lodash'

export interface StatisticalAnalysisExternalListPortion {
    caseStudy: StatisticalAnalysisExternalListCase
    sample: StatisticalAnalysisExternalListSample
    depleted: boolean
    generationDate: string
    generationPersonnel: CommonPersonnelResponse
    hasAttachments: boolean
    hasIncidents: boolean
    hasRevisions: boolean
    lastCutDate: string
    lastCutTechnician: CommonPersonnelResponse
    portionId: number
    portionType: CommonPortionTypeResponse
    processingEndDate: string
    processingEndPersonnel: CommonPersonnelResponse
    processingStartDate: string
    processingStartPersonnel: CommonPersonnelResponse,
    protocols: CommonProtocolResponse[]
    regenerationDate: string
    registrationDate: string
    state: CommonStatusResponse,
    laboratory: CommonLaboratoryResponse
}

export const StatisticalAnalysisExternalListColumnsPortions = ():ExportedColDef[] => {
    return [
        {
            type: AgGridColumnType.TEXT,
            headerName: i18n.tc('statisticalAnalysis.grids.portion.portionId'),
            field: 'label',
            exportType: AgGridExportType.TEXT,
            minWidth: 200,
            flex: 1,
            suppressSizeToFit: true,
            filter: false,
            sortable: false
        },
        {
            type: AgGridColumnType.TEXT,
            headerName: i18n.tc('statisticalAnalysis.grids.portion.portionType'),
            field: 'portionType.potName',
            exportType: AgGridExportType.TEXT,
            minWidth: 200,
            flex: 1,
            suppressSizeToFit: true,
            filter: false,
            sortable: false
        },
        {
            type: AgGridColumnType.DATETIME,
            headerName: i18n.tc('statisticalAnalysis.grids.portion.registryDate'),
            field: 'registrationDate',
            exportType: AgGridExportType.DATETIME,
            minWidth: 200,
            flex: 1,
            suppressSizeToFit: true,
            filter: false,
            sortable: false
        },
        {
            type: AgGridColumnType.DATETIME,
            headerName: i18n.tc('statisticalAnalysis.grids.portion.processingStartDate'),
            field: 'processingStartDate',
            exportType: AgGridExportType.DATETIME,
            minWidth: 200,
            hide: true,
            flex: 1,
            suppressSizeToFit: true,
            filter: false,
            sortable: false
        },
        {
            type: AgGridColumnType.DATETIME,
            headerName: i18n.tc('statisticalAnalysis.grids.portion.processingEndDate'),
            field: 'processingEndDate',
            exportType: AgGridExportType.DATETIME,
            minWidth: 200,
            hide: true,
            flex: 1,
            suppressSizeToFit: true,
            filter: false,
            sortable: false
        },
        {
            type: AgGridColumnType.DATETIME,
            headerName: i18n.tc('statisticalAnalysis.grids.portion.generationDate'),
            field: 'generationDate',
            exportType: AgGridExportType.DATETIME,
            minWidth: 200,
            flex: 1,
            hide: true,
            suppressSizeToFit: true,
            filter: false,
            sortable: false
        },
        {
            type: AgGridColumnType.TEXT,
            headerName: i18n.tc('statisticalAnalysis.grids.portion.processingStartPersonnel'),
            field: 'processingStartPersonnel.fullName',
            hide: true,
            exportType: AgGridExportType.TEXT,
            minWidth: 120,
            flex: 1,
            suppressSizeToFit: true,
            filter: false,
            sortable: false
        },
        {
            type: AgGridColumnType.TEXT,
            headerName: i18n.tc('statisticalAnalysis.grids.portion.processingEndPersonnel'),
            field: 'processingEndPersonnel.fullName',
            hide: true,
            exportType: AgGridExportType.TEXT,
            minWidth: 120,
            flex: 1,
            suppressSizeToFit: true,
            filter: false,
            sortable: false
        },
        {
            type: AgGridColumnType.TEXT,
            headerName: i18n.tc('statisticalAnalysis.grids.portion.generationPersonnel'),
            field: 'generationPersonnel.fullName',
            hide: true,
            exportType: AgGridExportType.TEXT,
            minWidth: 120,
            flex: 1,
            suppressSizeToFit: true,
            filter: false,
            sortable: false
        },
        {
            type: AgGridColumnType.TEXT,
            headerName: i18n.tc('statisticalAnalysis.grids.portion.lastCutTechnician'),
            field: 'lastCutTechnician.fullName',
            exportType: AgGridExportType.TEXT,
            minWidth: 120,
            flex: 1,
            hide: true,
            suppressSizeToFit: true,
            filter: false,
            sortable: false
        },
        {
            type: AgGridColumnType.TEXT,
            headerName: i18n.tc('statisticalAnalysis.grids.portion.protocols'),
            field: 'protocols',
            exportType: AgGridExportType.SET,
            minWidth: 200,
            flex: 1,
            hide: true,
            suppressSizeToFit: true,
            filter: false,
            sortable: false,
            cellRenderer: ({ data }) => {
                const protocols = data?.protocols
                if (protocols) {
                    return join(protocols, '; ')
                }
                return ''
            }
        },
        {
            type: AgGridColumnType.COMMON_PORTION_STATUS,
            headerName: i18n.tc('statisticalAnalysis.grids.portion.state'),
            field: 'state',
            exportField: 'state.name',
            exportType: AgGridExportType.TEXT,
            minWidth: 200,
            flex: 1,
            suppressSizeToFit: true,
            filter: false,
            sortable: false
        },
        {
            type: AgGridColumnType.CHECK_ICON,
            headerName: i18n.tc('statisticalAnalysis.grids.portion.hasRevisions'),
            field: 'hasRevisions',
            exportType: AgGridExportType.BOOLEAN,
            hide: true,
            minWidth: 200,
            flex: 1,
            suppressSizeToFit: true,
            filter: false,
            sortable: false
        },
        {
            type: AgGridColumnType.CHECK_ICON,
            headerName: i18n.tc('statisticalAnalysis.grids.portion.hasIncidents'),
            field: 'hasIncidents',
            exportType: AgGridExportType.BOOLEAN,
            hide: true,
            minWidth: 200,
            flex: 1,
            suppressSizeToFit: true,
            filter: false,
            sortable: false
        },
        {
            type: AgGridColumnType.CHECK_ICON,
            headerName: i18n.tc('statisticalAnalysis.grids.portion.hasAttachments'),
            field: 'hasAttachments',
            exportType: AgGridExportType.BOOLEAN,
            hide: true,
            minWidth: 200,
            flex: 1,
            suppressSizeToFit: true,
            filter: false,
            sortable: false
        },
        {
            type: AgGridColumnType.CHECK_ICON,
            headerName: i18n.tc('statisticalAnalysis.grids.portion.depleted'),
            field: 'depleted',
            exportType: AgGridExportType.BOOLEAN,
            hide: true,
            minWidth: 200,
            flex: 1,
            suppressSizeToFit: true,
            filter: false,
            sortable: false
        }
    ]
}

export const StatisticalAnalysisExternalListPortions = (): (ColDef[] | ColGroupDef[] | ExportedColGroupDef[] | ExportedColDef []) => {
    return [
        {
            headerName: i18n.tc('statisticalAnalysis.grids.groupCases'),
            children: StatisticalAnalysisExternalListColumnsCases().map((item:ExportedColDef) => {
                item.field = `caseStudy.${item.field}`
                if (item.exportField) {
                    item.exportField = `caseStudy.${item.exportField}`
                }
                return item
            })
        },
        {
            headerName: i18n.tc('statisticalAnalysis.grids.groupSamples'),
            children: StatisticalAnalysisExternalListColumnsSample().map((item:ExportedColDef) => {
                item.field = `sample.${item.field}`
                if (item.exportField) {
                    item.exportField = `sample.${item.exportField}`
                }
                return item
            })
        },
        {
            headerName: i18n.tc('statisticalAnalysis.grids.portion.groupPortions'),
            children: StatisticalAnalysisExternalListColumnsPortions()
        }
    ]
}
